import { type CSSProperties, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Link2, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { APP_URL } from '@/data/marketingContent';
import { cn } from '@/lib/utils';
import { AnimatedNumber, EASE, Reveal } from '@/components/home/primitives';
import { calculateScreeningCost, clamp, DEFAULT_INPUTS, formatMoney, LIMITS, PRO_ANNUAL_MONTHLY_USD, SYMBOL, WORKDAY_HOURS, type Currency, type ScreeningInputs } from './screeningCost';


function RangeField({ id, label, hint, value, onChange, format, limits }: { id: string; label: string; hint: string; value: number; onChange: (v: number) => void; format: (v: number) => string; limits: { min: number; max: number; step: number } }) {
  const pct = ((value - limits.min) / (limits.max - limits.min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-[15px] font-semibold text-bv-ink">{label}</label>
        <output htmlFor={id} className="font-display text-xl font-semibold tabular-nums text-bv-ink">{format(value)}</output>
      </div>
      <p className="mt-0.5 text-[13px] text-bv-mute">{hint}</p>
      <input id={id} type="range" min={limits.min} max={limits.max} step={limits.step} value={value} onChange={e => onChange(Number(e.target.value))} className="bv-range mt-4 w-full" style={{ '--pct': `${pct}%` } as CSSProperties} />
    </div>
  );
}

/**
 * Interactive screening cost calculator.
 * `shareable` stores the inputs in the URL (?r=&a=&m=&h=&c=) and shows copy/share controls.
 */
export function ScreeningCalculator({ shareable = false, idPrefix = 'calc' }: { shareable?: boolean; idPrefix?: string }) {
  const [inputs, setInputs] = useState<ScreeningInputs>(DEFAULT_INPUTS);
  const [currency, setCurrency] = useState<Currency>('USD');
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    if (!shareable) return;
    const params = new URLSearchParams(window.location.search);
    setInputs(prev => ({
      roles: clamp('roles', params.get('r')) ?? prev.roles,
      applications: clamp('applications', params.get('a')) ?? prev.applications,
      minutes: clamp('minutes', params.get('m')) ?? prev.minutes,
      rate: clamp('rate', params.get('h')) ?? prev.rate,
    }));
    const c = params.get('c');
    if (c === 'USD' || c === 'GBP' || c === 'EUR') setCurrency(c);
  }, [shareable]);

  useEffect(() => {
    if (!shareable) return;
    const params = new URLSearchParams({ r: String(inputs.roles), a: String(inputs.applications), m: String(inputs.minutes), h: String(inputs.rate), c: currency });
    const url = `${window.location.pathname}?${params}`;
    window.history.replaceState(window.history.state, '', url);
    setShareUrl(`${window.location.origin}${url}`);
  }, [shareable, inputs, currency]);

  const set = (key: keyof ScreeningInputs) => (value: number) => setInputs(prev => ({ ...prev, [key]: value }));
  const { total, hours, days, cost } = calculateScreeningCost(inputs);
  const symbol = SYMBOL[currency];
  const barMax = Math.max(cost, PRO_ANNUAL_MONTHLY_USD);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl || window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <Reveal className="min-w-0">
        <div className="h-full space-y-8 rounded-3xl border border-bv-blue-line bg-bv-blue-wash/50 p-6 sm:p-9">
          {shareable && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[15px] font-semibold text-bv-ink">Currency</span>
              <div role="group" aria-label="Currency" className="inline-flex rounded-full border border-bv-blue-line bg-white p-1">
                {(['USD', 'GBP', 'EUR'] as const).map(c => (
                  <button key={c} type="button" aria-pressed={currency === c} onClick={() => setCurrency(c)} className={cn('rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors', currency === c ? 'bg-bv-ink text-white' : 'text-bv-ink2 hover:text-bv-ink')}>{SYMBOL[c]} {c}</button>
                ))}
              </div>
            </div>
          )}
          <RangeField id={`${idPrefix}-roles`} label="Roles advertised per month" hint="Across every consultant on the desk" value={inputs.roles} onChange={set('roles')} format={v => `${v}`} limits={LIMITS.roles} />
          <RangeField id={`${idPrefix}-applications`} label="Applications per role" hint="Average inbound applications for one role" value={inputs.applications} onChange={set('applications')} format={v => `${v}`} limits={LIMITS.applications} />
          <RangeField id={`${idPrefix}-minutes`} label="Minutes to screen one application" hint="Open, read, decide, update the record" value={inputs.minutes} onChange={set('minutes')} format={v => `${v} min`} limits={LIMITS.minutes} />
          <RangeField id={`${idPrefix}-rate`} label="Fully loaded recruiter cost per hour" hint="Salary, commission, benefits and overhead" value={inputs.rate} onChange={set('rate')} format={v => `${symbol}${v}`} limits={LIMITS.rate} />
        </div>
      </Reveal>
      <Reveal delay={0.1} className="min-w-0">
        <div className="relative h-full overflow-hidden rounded-3xl bg-bv-ink p-6 text-white sm:p-9" aria-live="polite">
          <div aria-hidden className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[hsl(var(--hero-highlight-start)/0.3)] blur-[90px]" />
          <div aria-hidden className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[hsl(var(--blue)/0.3)] blur-[90px]" />
          <p className="relative text-sm font-semibold text-white/60">Every month your desk reads</p>
          <p className="relative mt-1 font-display text-2xl font-semibold"><AnimatedNumber value={total} format={v => Math.round(v).toLocaleString('en-US')} /> applications by hand</p>
          <div className="relative mt-7 border-t border-white/10 pt-7">
            <p className="text-sm font-semibold text-white/60">That is</p>
            <p className="mt-1 font-display text-6xl font-semibold tracking-[-0.04em] sm:text-7xl">
              <span className="bv-text-gradient-dark tabular-nums"><AnimatedNumber value={hours} format={v => Math.round(v).toLocaleString('en-US')} /></span>
              <span className="ml-3 text-2xl tracking-normal text-white/70">hours</span>
            </p>
            <p className="mt-2 text-white/70">of recruiter time spent on first-pass screening</p>
          </div>
          <div className="relative mt-7 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/[0.06] p-4"><p className="font-display text-3xl font-semibold tabular-nums"><AnimatedNumber value={days} format={v => v.toFixed(1)} /></p><p className="mt-1 text-[13px] text-white/60">working days ({WORKDAY_HOURS}h)</p></div>
            <div className="rounded-2xl bg-white/[0.06] p-4"><p className="font-display text-3xl font-semibold tabular-nums"><AnimatedNumber value={cost} format={v => formatMoney(v, currency)} /></p><p className="mt-1 text-[13px] text-white/60">in loaded staff cost</p></div>
          </div>
          {currency === 'USD' ? (
            <div className="relative mt-7 space-y-3" aria-hidden>
              {[{ label: 'Screening time, per month', value: cost, className: 'bv-btn-gradient' }, { label: 'Beatview Pro, billed annually', value: PRO_ANNUAL_MONTHLY_USD, className: 'bg-bv-blue-bright' }].map(bar => (
                <div key={bar.label}>
                  <div className="flex justify-between text-[13px] text-white/70"><span>{bar.label}</span><span className="tabular-nums">{formatMoney(bar.value)}</span></div>
                  <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-white/10"><motion.div className={cn('h-full rounded-full', bar.className)} animate={{ width: `${Math.max((bar.value / barMax) * 100, 2)}%` }} transition={{ duration: 0.6, ease: EASE }} /></div>
                </div>
              ))}
            </div>
          ) : (
            <p className="relative mt-7 rounded-2xl bg-white/[0.06] p-4 text-[13px] text-white/70">For comparison, Beatview Pro is $179 a month billed annually (USD).</p>
          )}
          <p className="relative mt-6 text-[12px] leading-5 text-white/50">Hours = roles × applications × minutes ÷ 60. This is an estimate from your inputs, not a savings guarantee — recruiters still review the agent’s work.</p>
          {shareable && (
            <div className="relative mt-6 grid grid-cols-2 gap-2">
              <button type="button" onClick={copyLink} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                {copied ? <><Check className="h-4 w-4" />Link copied</> : <><Link2 className="h-4 w-4" />Copy link to results</>}
              </button>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl || 'https://beatview.ai/screening-cost-calculator')}`} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 text-sm font-semibold text-white transition-colors hover:bg-white/10"><Linkedin className="h-4 w-4" />Share on LinkedIn</a>
            </div>
          )}
          <Button asChild className="bv-shine relative mt-3 h-auto min-h-12 w-full whitespace-normal rounded-xl py-3 text-center text-base font-semibold"><a href={APP_URL}>Put the agent on those applications <ArrowRight className="h-4 w-4" /></a></Button>
        </div>
      </Reveal>
    </div>
  );
}
